<section class="care-plan-picker" [attr.aria-labelledby]="titleId">
  <h3 [id]="titleId" class="care-plan-picker__title">
    Items Worked on<span *ngIf="rowLabel" class="cdk-visually-hidden">, {{ rowLabel }}</span>
  </h3>
  <p class="care-plan-picker__hint">
    All active care plans of the patient. For each plan, tick the goal and/or its interventions you worked on, and say
    in one line what you did. Tick Care coordination or Other for work outside a care plan.
  </p>

  <ng-container *ngIf="status === 'ready'">
    <div
      *ngFor="let plan of plans; trackBy: trackPlan"
      class="care-plan-picker__plan"
      [class.care-plan-picker__plan--frozen]="plan.frozen"
    >
      <div class="care-plan-picker__plan-header">
        <h4 class="care-plan-picker__plan-name">Care plan: {{ planTitle(plan) }}</h4>
        <span class="care-plan-picker__plan-dx">{{ plan.dxList.join(', ') }}</span>
      </div>
      <p *ngIf="plan.frozen" class="care-plan-picker__frozen-note">
        No longer an active care plan. Shown as it was saved on this entry: you can untick it, not pick from it again.
      </p>
      <p *ngIf="!plan.goals.length" class="care-plan-picker__frozen-note">This care plan has no open goals.</p>

      <div *ngFor="let goal of plan.goals; let i = index; trackBy: trackGoal" class="care-plan-picker__goal">
        <span *ngIf="showsProblem(plan, i)" class="care-plan-picker__problem">{{
          goal.problemTitle || 'Problem'
        }}</span>
        <mat-checkbox
          class="care-plan-picker__box"
          [checked]="isTicked(plan, goal, 'goal')"
          [disabled]="!canChange(plan, goal, 'goal')"
          (change)="toggle(plan, goal, 'goal', $event.checked)"
        >
          <span class="care-plan-picker__chip care-plan-picker__chip--goal">Goal</span>
          <span class="care-plan-picker__goal-text">{{ goal.goalText || 'Untitled goal' }}</span>
        </mat-checkbox>
        <p *ngIf="goal.frozen && !plan.frozen" class="care-plan-picker__frozen-note">
          No longer in this care plan. Shown as it was saved on this entry.
        </p>

        <div class="care-plan-picker__interventions">
          <mat-checkbox
            class="care-plan-picker__box"
            [checked]="isTicked(plan, goal, 'interventions')"
            [disabled]="!canChange(plan, goal, 'interventions')"
            [aria-label]="'Interventions on goal: ' + (goal.goalText || 'Untitled goal')"
            (change)="toggle(plan, goal, 'interventions', $event.checked)"
          >
            <span class="care-plan-picker__chip care-plan-picker__chip--interventions">Interventions</span>
            Interventions on this goal
          </mat-checkbox>
          <ul *ngIf="goal.interventions.length; else noInterventions" class="care-plan-picker__intervention-list">
            <li *ngFor="let line of shownInterventions(plan, goal)">{{ line }}</li>
          </ul>
          <ng-template #noInterventions>
            <span class="care-plan-picker__frozen-note">No interventions on this goal.</span>
          </ng-template>
          <button
            *ngIf="goal.interventions.length > collapsedInterventions"
            mat-button
            type="button"
            color="accent"
            class="care-plan-picker__show-all"
            [attr.aria-expanded]="isExpanded(plan, goal)"
            (click)="toggleExpanded(plan, goal)"
          >
            {{ isExpanded(plan, goal) ? 'Show less' : 'Show all ' + goal.interventions.length }}
          </button>
        </div>
      </div>

      <mat-form-field *ngIf="hasPicks(plan)" class="care-plan-picker__note" hideRequiredMarker>
        <mat-label>What did you do on this plan? (required)</mat-label>
        <input
          matInput
          required
          [value]="noteOf(plan)"
          [disabled]="disabled"
          [attr.maxlength]="noteMaxLength"
          [attr.aria-label]="'What did you do on the ' + (plan.name || 'unnamed') + ' care plan? (required)'"
          placeholder="One line on what you reviewed or did"
          (input)="onNoteInput(plan, $event)"
          (blur)="onNoteBlur()"
        />
        <mat-hint *ngIf="isNoteMissing(plan)" class="care-plan-picker__note-missing">
          Required for every plan you picked from.
        </mat-hint>
      </mat-form-field>
    </div>
  </ng-container>

  <!-- Offered when the row needs picks and no plan has an open goal; Care coordination and Other follow it. -->
  <div *ngIf="showsCreateCarePlan" class="care-plan-picker__create">
    <p class="care-plan-picker__hint">
      {{ plans.length ? 'Add a goal to one of these care plans first.' : 'Create the care plan first.' }}
      Your time entry is kept and you come back here to finish it. Or, if the time was care coordination or other work,
      tick it below and say what you did.
    </p>
    <button
      mat-raised-button
      type="button"
      color="accent"
      class="care-plan-picker__create-button"
      (click)="createCarePlan.emit()"
    >
      {{ plans.length ? 'Go to Care Plans' : 'Create care plan' }}
    </button>
  </div>

  <ng-container *ngIf="status === 'ready'">
    <!-- After the plans and after the create block, so the no plan state gets them too. -->
    <div *ngFor="let activity of activities; trackBy: trackActivity" class="care-plan-picker__extra">
      <mat-checkbox
        class="care-plan-picker__extra-box"
        [checked]="isActivityTicked(activity.key)"
        [disabled]="disabled"
        (change)="toggleActivity(activity.key, $event.checked)"
      >
        {{ activity.label }}
      </mat-checkbox>
      <mat-form-field *ngIf="isActivityTicked(activity.key)" class="care-plan-picker__note" hideRequiredMarker>
        <mat-label>{{ activity.prompt }}</mat-label>
        <input
          matInput
          required
          [value]="activityNoteOf(activity.key)"
          [disabled]="disabled"
          [attr.maxlength]="noteMaxLength"
          [attr.aria-label]="activity.prompt"
          [placeholder]="activity.placeholder"
          (input)="onActivityNoteInput(activity.key, $event)"
          (blur)="onNoteBlur()"
        />
        <mat-hint *ngIf="isActivityNoteMissing(activity.key)" class="care-plan-picker__note-missing">
          Required when ticked.
        </mat-hint>
      </mat-form-field>
    </div>
  </ng-container>

  <!-- Always in the DOM, so screen readers announce each new message instead of missing an inserted region. -->
  <p
    #statusLine
    class="care-plan-picker__status"
    [class.care-plan-picker__status--warn]="statusIsWarning"
    role="status"
    tabindex="-1"
  >
    {{ statusMessage }}
  </p>
  <button
    *ngIf="status === 'error'"
    mat-button
    type="button"
    color="accent"
    class="care-plan-picker__retry"
    (click)="onRetry(statusLine)"
  >
    Try again
  </button>
</section>
